import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { COMPANY_STATUS_OPTIONS } from "../constants";
import { INDUSTRY_OPTIONS } from "@/components/ui/selects/options/contact";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

/**
 * Saved-view ids every basic identity field belongs to (status-* + recent + all + clients-* + tickets-*).
 * Centralised so per-field arrays stay short and grep-friendly.
 */
const ALL_BASE_VIEWS = [
  "all-companies",
  "recent-companies",
  "status-active",
  "status-inactive",
  "status-prospect",
  "status-client",
  "status-former-client",
  "status-suspended",
  "clients-with-active-deals",
  "clients-without-active-deals",
  "no-tickets-this-month",
  "with-tickets-this-month",
  "no-tickets-last-month",
  "with-tickets-last-month",
  "under-contract",
  "no-contract",
  "contract-expiring-soon",
  "with-open-tickets",
  "cold-90-days",
] as const;

export const companyBasicFields = defineFieldConfigMap({
    // FIELD: name
    name: {
      type: "text",
      label: "Name",
      mobileDetailSectionId: "basic",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 3,
      filterType: "contains",
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      required: true,
      validation: {
        required: "Company name is required.",
      },
      placeholder: "Enter company name",
      icon: "Building2",
      // Role-based access control - visible to all roles
      allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
      // Show row actions inline with this column
      showRowActions: true,
      showOnCard: true,
      cardSpan: 2,
      graphqlSelector: "name",
      savedViewMembership: ALL_BASE_VIEWS,
    },
    // FIELD: normalized_name
    normalized_name: {
      type: "text",
      label: "Normalized Name",
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false, // Auto-generated
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Auto-generated",
      icon: "Settings",
      excludeFromApi: true,
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "normalized_name",
    },
    // FIELD: status
    status: {
      type: "select",
      label: "Status",
      mobileDetailSectionId: "basic",
      isStatusField: true,
      sortable: true,
      filterable: true,
      searchable: false, // Select fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing for status
      formSpan: 1,
      filterType: "exact",
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      placeholder: "Select status",
      icon: "Circle",
      options: COMPANY_STATUS_OPTIONS,
      groupable: true,
      // Role-based access control - admin only for status changes
      allowedRoles: ["wtc_admin"],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "status",
      savedViewMembership: ALL_BASE_VIEWS,
    },
    // FIELD: has_active_deals
    has_active_deals: {
      type: "boolean",
      label: "Has Deals",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      excludeFromApi: true,
      isReadOnlyDerived: true,
      readOnlyReason: "Maintained automatically from active deals for this company.",
      dataLineage: {
        kind: "trigger-populated",
        sourceTable: "deals",
        sourceField: "stage",
        sourceForeignKeyField: "company_id",
        notes: "True when at least one related deal is in an active sales pipeline stage.",
      },
      filterType: "exact",
      width: 115,
      minWidth: 95,
      maxWidth: 140,
      icon: "Briefcase",
      groupable: true,
      allowedRoles: [...ALL_STAFF_ROLES],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "has_active_deals",
      savedViewMembership: [
        "all-companies",
        "recent-companies",
        "status-client",
        "clients-with-active-deals",
        "clients-without-active-deals",
      ],
    },
    // FIELD: active_deal_count
    active_deal_count: {
      type: "number",
      label: "Active Deals",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      excludeFromApi: true,
      isReadOnlyDerived: true,
      readOnlyReason: "Maintained automatically from active deals for this company.",
      dataLineage: {
        kind: "trigger-populated",
        sourceTable: "deals",
        sourceField: "id",
        sourceForeignKeyField: "company_id",
        notes: "Count of related deals in active sales pipeline stages.",
      },
      filterType: "range",
      width: 125,
      minWidth: 105,
      maxWidth: 150,
      icon: "Briefcase",
      groupable: true,
      allowedRoles: [...ALL_STAFF_ROLES],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "active_deal_count",
      savedViewMembership: ["all-companies", "status-client", "clients-with-active-deals"],
    },
    // FIELD: total_deal_value
    total_deal_value: {
      type: "number",
      label: "Active Deal Value",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      excludeFromApi: true,
      isReadOnlyDerived: true,
      readOnlyReason: "Maintained automatically from active deals for this company.",
      dataLineage: {
        kind: "trigger-populated",
        sourceTable: "deals",
        sourceField: "revenue",
        sourceForeignKeyField: "company_id",
        notes: "Sum of revenue for related deals in active sales pipeline stages.",
      },
      filterType: "range",
      width: 150,
      minWidth: 125,
      maxWidth: 190,
      icon: "DollarSign",
      groupable: true,
      allowedRoles: [...ALL_STAFF_ROLES],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "total_deal_value",
      savedViewMembership: ["all-companies", "status-client", "clients-with-active-deals"],
    },
    // FIELD: industry
    industry: {
      type: "select",
      label: "Industry",
      mobileDetailSectionId: "basic",
      sortable: true,
      filterable: true,
      searchable: false, // Select fields typically not searchable
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true, // Enable bulk editing
      formSpan: 1,
      filterType: "exact",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      placeholder: "Select industry",
      icon: "Building2",
      options: INDUSTRY_OPTIONS,
      groupable: true,
      // Role-based access control - visible to all roles
      allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
      showOnCard: true,
      cardSpan: 1,
      graphqlSelector: "industry",
      savedViewMembership: ALL_BASE_VIEWS,
    }
});
